import React, { useCallback, useMemo } from 'react'

import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import {
  selectNewEmailAiAgentId,
  selectNewEmailCitationIsLoading,
  selectNewEmailEditing,
  selectNewEmailExecutionLoading,
  selectNewEmailInteractionLoading,
  selectNewEmailResult,
  setNewEmailAiAgentId,
  setNewEmailInteractionLoading,
  setNewEmailResult,
} from '../../../store/emailHub/newEmail'

import { NewEmailResult } from '../../../models/NewEmail'

import { isNewEmailNoResult } from './utils'
import { addMessage, selectLastUserChatMessage, updateStreamingMessage } from '../../../store/streamingChat/streamingChat'
import { StreamingAIAgentChat } from '../../AIAgent/Chat/StreamingAIAgentChat'
import { useStreamingAiAgent } from '../../AIAgent/hooks/useStreamingAiAgent'

export interface NewEmailChatProps {
  headerOnly?: boolean
}

export const NewEmailChat: React.FC<NewEmailChatProps> = ({ headerOnly = false }) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const isLoading = useAppSelector(selectNewEmailInteractionLoading)
  const executionIsLoading = useAppSelector(selectNewEmailExecutionLoading)
  const isEditing = useAppSelector(selectNewEmailEditing)
  const aiAgentId = useAppSelector(selectNewEmailAiAgentId)
  const citationIsLoading = useAppSelector(selectNewEmailCitationIsLoading)
  const newEmailResult = useAppSelector(selectNewEmailResult)
  const lastUserInteraction = useAppSelector(selectLastUserChatMessage)

  const isNoResult = useMemo(() => isNewEmailNoResult(newEmailResult), [newEmailResult])

  const onTextDelta = useCallback(
    (data: NewEmailResult) => {
      dispatch(setNewEmailResult(data))
      const { message } = data
      if (message) {
        dispatch(updateStreamingMessage(message))
      }
    },
    [dispatch],
  )

  const onNewAiAgentId = useCallback(
    (newAiAgentId: string) => {
      dispatch(setNewEmailAiAgentId(newAiAgentId))
    },
    [dispatch],
  )

  const { triggerInteractWithAiAgentStreaming } = useStreamingAiAgent<NewEmailResult>({ onTextDelta, onNewAiAgentId })

  const handleNewEmailChatInteraction = async (chatMessage: string) => {
    if (!aiAgentId) {
      return
    }
    dispatch(setNewEmailInteractionLoading(true))
    try {
      dispatch(addMessage({ type: 'user', message: chatMessage }))
      await triggerInteractWithAiAgentStreaming({
        accountId,
        aiAgentId,
        payload: { type: 'chat', query: chatMessage },
      })
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setNewEmailInteractionLoading(false))
    }
  }

  const handleResubmit = useCallback(async () => {
    if (!aiAgentId || !lastUserInteraction) {
      return
    }
    dispatch(setNewEmailInteractionLoading(true))
    try {
      await triggerInteractWithAiAgentStreaming({
        accountId,
        aiAgentId,
        payload: { type: 'chat', query: lastUserInteraction.message },
      })
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setNewEmailInteractionLoading(false))
    }
  }, [accountId, aiAgentId, dispatch, lastUserInteraction, triggerInteractWithAiAgentStreaming])

  return (
    <div>
      <StreamingAIAgentChat
        handleSubmit={handleNewEmailChatInteraction}
        handleResubmit={handleResubmit}
        headerOnly={headerOnly}
        isLoading={isLoading}
        disabled={isEditing || isNoResult || executionIsLoading || citationIsLoading}
      />
    </div>
  )
}
